import { Injectable } from "@angular/core";

export interface TutionLocation {
  id: string;
  town: string;
  address: string;
  postcode: string;
  phone: string;
  email: string;
  image: string;
  badge?: string;
  distance?: number;
  services: string[];
  description: string;
  openingHours: { day: string; time: string }[];
  photos: string[];
  faqs: { question: string; answer: string }[];
  mapUrl: string;

  schedules?: CentreSchedule[];
}

export interface MockSchedule {
  centre: string;
  category: string;
  rows: { test: string; date: string }[];
}

export interface ScheduleRow {
  test: string;
  date: string;
  link?: string;
}

export interface CentreSchedule {
  title: string;
  rows: ScheduleRow[];
}

@Injectable({
  providedIn: "root",
})
export class LocationService {
  private locations: TutionLocation[] = [
    //Chelmford
    {
      id: "essex-chelmsford-11plus-mocks",
      town: "Chelmsford",
      address:
        "The Boswells School, Burnham Rd, Springfield, Chelmsford CM1 6LY",
      postcode: "CM1 6LY",
      phone: "(0)2039808244",
      email: "support@sehyog.co.uk",
      image:
        "https://hcgroup.uk/wp-content/uploads/2021/09/cchsfg-sports-hall-and-teaching-block-097.jpg",
      badge: "Popular",
      distance: 2.5,
      services: [
        "11+ Mock Exams",
        "GCSE Mock Exams",
        "1-2-1 & Group Tuitions (11+, KS3, GCSE)",
      ],
      description:
        "Located in Chelmsford, this exam centre offers a quiet, structured environment ideal for focused assessment. Easy accessibility and a professional setting ensure a smooth and supportive exam experience for all students.",
      openingHours: [
        { day: "Monday", time: "9:00 AM - 7:00 PM" },
        { day: "Tuesday", time: "9:00 AM - 7:00 PM" },
        { day: "Wednesday", time: "9:00 AM - 7:00 PM" },
        { day: "Thursday", time: "9:00 AM - 7:00 PM" },
        { day: "Friday", time: "9:00 AM - 6:00 PM" },
        { day: "Saturday", time: "9:00 AM - 7:00 PM" },
        { day: "Sunday", time: "9:00 AM - 7:00 PM" },
      ],
      photos: [
        "assets/images/chelmsford-1.jpg",
        "assets/images/chelmsford-2.jpg",
        "assets/images/chelmsford-3.jpg",
        "assets/images/chelmsford-4.jpg",
      ],
      faqs: [
        {
          question: "Is there parking available?",
          answer: "Yes, ample parking available at the center.",
        },
        {
          question: "What subjects are covered in the mock exams?",
          answer:
            "For 11+, mocks usually include English, Mathematics, Verbal Reasoning, and Non-Verbal/Spatial Reasoning (depending on the exam board). GCSE mocks are offered in core subjects such as English, Mathematics, Computer Science, and Science, with additional subjects available at selected centres.",
        },
        {
          question: "Are the mock exams based on real exam formats?",
          answer:
            "Yes. All mock papers are designed to closely reflect the structure, timing, and difficulty level of the actual 11+ and GCSE examinations.",
        },
        {
          question: "Who writes the mock exam papers?",
          answer:
            "Papers are created by experienced subject specialists and reviewed to ensure accuracy, relevance, and appropriate exam standards.",
        },
        {
          question: "Are the mocks suitable for all ability levels?",
          answer:
            "Yes. The mock exams are designed to be accessible while still providing meaningful challenge and insight into a student’s current exam readiness.",
        },
        {
          question: "How do I book a mock exam?",
          answer:
            "Bookings can be made online via our website www.sehyog.co.uk. Places are limited and allocated on a first-come, first-served basis.",
        },
        {
          question: "Can I choose a specific exam centre?",
          answer:
            "Yes. Parents can select their preferred centre at the time of booking, subject to availability.",
        },
        {
          question: "What is included in the mock exam fee?",
          answer:
            "The fee includes the exam sitting, professional invigilation, marking, a detailed results report, and a doubt session for 11+ candidates only.",
        },
        {
          question: "Are refunds available if my child cannot attend?",
          answer:
            "Refund and rescheduling policies are clearly outlined during the booking process and vary depending on the notice given.",
        },
        {
          question: "What should students bring on the exam day?",
          answer:
            "Students should bring required stationery (pens, pencils, ruler, calculator where permitted) and their exam confirmation details.",
        },
        {
          question: "How closely do you follow real exam conditions?",
          answer:
            "Mock exams are conducted under strict exam conditions, including timed papers and formal invigilation, to help students build confidence and familiarity.",
        },
        {
          question: "How long do the exams last?",
          answer:
            "Exam durations match real exam timings and vary depending on the subject and level (11+ or GCSE).",
        },
        {
          question: "When will results be available?",
          answer:
            "Results are usually released within a specified timeframe after the exam (e.g. 3–5 days), depending on the exam type and number of students participating. In most cases, results are shared within 48 hours (T&C apply).",
        },
        {
          question: "What feedback will we receive?",
          answer:
            "Parents receive a detailed performance breakdown including scores, rank, percentile, average score, max/min score, strengths, areas for improvement, and overall exam readiness.",
        },
        {
          question: "Will rankings or comparisons be provided?",
          answer:
            "Where appropriate, results may include cohort comparisons or percentile rankings to give context to performance.",
        },
        {
          question:
            "Can the mock results be used to guide further preparation?",
          answer:
            "Absolutely. The results are designed to help identify gaps in knowledge and inform targeted revision or tuition plans.",
        },
        {
          question: "Do you offer follow-up support after the mocks?",
          answer:
            "Yes. Optional feedback sessions, revision workshops, or tuition may be available depending on the centre.",
        },
      ],

      schedules: [
        {
          title: "11+ CSSE Mock Tests",
          rows: [
            {
              test: "CSSE 1st SET",
              date: "Saturday, 14 March 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/8312374608af53c7a401dbf30e8d7c82",
            },
            {
              test: "CSSE 2nd SET",
              date: "Saturday, 18 April 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/efa2035b32f9c302a79cddd4d925223a",
            },
            {
              test: "CSSE 3rd SET",
              date: "Saturday, 09 May 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/51a22a375235ae34a22a0a172bcb812e",
            },
            {
              test: "CSSE 4th SET",
              date: "Saturday, 06 June 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/e5abf16dd34d47fc1d100aa080664104",
            },
            {
              test: "CSSE 5th SET",
              date: "Saturday, 27 June 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/e5abf16dd34d47fc1d100aa080664104",
            },
            {
              test: "CSSE 6th SET",
              date: "Saturday, 04 July 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/c9a147778c513287ccadb79be6980554",
            },
            {
              test: "CSSE 7th SET",
              date: "Saturday, 18 July 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/e8b2ac228d762a882be14b971d871ab7",
            },
            {
              test: "CSSE 8th SET",
              date: "Saturday, 08 August 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/e9dcbf2b5bb281e8e25b1ad36047d183",
            },
            {
              test: "CSSE 9th SET",
              date: "Saturday, 15 August 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/0f7d3c705b3712a2e453f6e97651dfe7",
            },
            {
              test: "CSSE 10th SET",
              date: "Saturday, 29 August 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/23923f483c337cd3ea599498cb7338c3",
            },
            {
              test: "CSSE 11th SET",
              date: "Saturday, 12 September 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/950193abfc29b42b4739e8b065ab2ee6",
            },
          ],
        },
        {
          title: "11+ FSCE Mock Tests (CCHS Girls)",
          rows: [
            {
              test: "FSCE 1st SET",
              date: "Saturday, 11 April",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/16f860322acca74ec0d5d045e10536d3",
            },
            {
              test: "FSCE 2nd SET",
              date: "Saturday, 02 May",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/2b8599369f9cfdf43ea7b04305c97f0d",
            },
            {
              test: "FSCE 3rd SET",
              date: "Saturday, 30 May",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/a7888dc227768565c0992d7a88441e16",
            },
            {
              test: "FSCE 4th SET",
              date: "Saturday, 20 June",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/a7888dc227768565c0992d7a88441e16",
            },
            {
              test: "FSCE 5th SET",
              date: "Saturday, 11 July",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/b893abd24185122d81060fc3369c9add",
            },
            {
              test: "FSCE 6th SET",
              date: "Saturday, 01 August",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/7e1ffef1bf82f1d16c0307dc503b86e1",
            },
            {
              test: "FSCE 7th SET",
              date: "Saturday, 22 August",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/ae156f7b35d1087833a698d9d73b7c9c",
            },
          ],
        },

        //Year 4
        {
          title: "11+ Year 4 Topic Tests",
          rows: [
            {
              test: "Maths Numbers & Factors (M01) & English Reading Comprehension (ECOMP01 & CW - Topic Tests ",
              date: "Saturday, March 14, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/1f161a414d4f510377d209417606cd83",
            },
            {
              test: "Maths Fractions (M03) & Verbal Non-Verbal Reasoning (VRNVR01) or CW01 - Topic Tests",
              date: "Saturday, April 11, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/2d994ad2c1296f79605697e8477a77cc",
            },
            {
              test: "Maths Fractions (M03) & Verbal Non-Verbal Reasoning (VRNVR01) or CW01 - Topic Tests",
              date: "Saturday, April 18, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/1ba8f9ce03634fdd464343b8ac9bbf03",
            },
            {
              test: "Maths Percentage (M04) & English Reading Comprehension (ECOMP03) & CW",
              date: "Saturday, April 18, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/2d994ad2c1296f79605697e8477a77cc",
            },

            {
              test: "Maths Data Handling (M06) & VR/NVR (VRNVR02) or CW02",
              date: "Saturday, May 2, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/54567792a0a7fcd0d764134a6078eb19",
            },

            {
              test: "Maths Day Date Time (M07) & English Comprehension (ECOMP05) & CW",
              date: "Saturday, May 9, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/1ff5516c6322abf02333d167c1fec6dc",
            },

            {
              test: "Maths Shapes, Area, Perimeter & Volume Part 01 (M09) & Eng Reading Comp (ECOMP07) & CW",
              date: "Saturday, May 30, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/ac127dd06439b3f0fc686a9aa8151a30",
            },
//--------------------
            {
              test: "Maths Algebra Part 01 (M10) & Creative Writing (CW03) & VRNVR03 - Topic Tests ",
              date: "Saturday, June 6, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/2d0d1b5156695f6c205ab67ad4a535ad",
            },

            {
              test: "Maths Data Handling Part#2 (M12) & CW04 or VRNVR04 ",
              date: "Saturday, June 20, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/0a96d914f0e4c903b8f0648620fd2185",
            },


            {
              test: "Maths Shapes, Area, Perimeter & Volume Part#2 (M13) & English Reading Comprehension (ECOMP09)",
              date: "Saturday, June 27, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/0611c0c10ddc279c61adc5e9785f691e",
            },
            {
              test: "Maths Algebra Part 2 (M14) & 10th English Comprehension CSSE Style (ECOMP10)",
              date: "Saturday, July 4, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/add8ea7e043bf6ef69da3f5f9aeb5643",
            },
            {
              test: "ALL Topics - Maths + Creative Writing05 or VRNVR05",
              date: "Saturday, July 11, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/93e8a86640296e55d75f3e803b4242e9",
            },
          ],
        },

      ],
      mapUrl:
        "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d39458.14637312834!2d0.4485967!3d51.7356167!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47d8c4e9c5e5e5e5%3A0x5c5e5e5e5e5e5e5!2sChelmsford%2C%20UK!5e0!3m2!1sen!2sus!4v1234567890",
    },

    //Ilford

    {
      id: "redbridge-ilford-11plus-mocks",
      town: "Ilford",
      address:
        "Oaks Park School (Main Gate), Block B, Near Sports complex, First Floor, Room C9",
      postcode: "IG2 7PR",
      phone: "(0)2039808244",
      email: "support@sehyog.co.uk",
      image:
        "https://www.rivingtonstreetstudio.co.uk/wordpress_2016/wp-content/uploads/RACHELFERRIMAN-RSS-LITTLE-ILFORD-LOWRES-034.jpg",
      distance: 5.3,
      services: ["11+ Mock Exams", "GCSE Mock Exams"],
      description:
        "Our Ilford exam centre is conveniently situated with excellent public transport links. It provides a calm, well-organised setting designed to support students of all abilities in achieving their best performance.",
      openingHours: [
        { day: "Monday", time: "9:00 AM - 7:00 PM" },
        { day: "Tuesday", time: "9:00 AM - 7:00 PM" },
        { day: "Wednesday", time: "9:00 AM - 7:00 PM" },
        { day: "Thursday", time: "9:00 AM - 7:00 PM" },
        { day: "Friday", time: "9:00 AM - 7:00 PM" },
        { day: "Saturday", time: "9:00 AM - 7:00 PM" },
        { day: "Sunday", time: "9:00 AM - 7:00 PM" },
      ],
      photos: [
        "assets/images/ilford-1.jpg",
        "assets/images/ilford-2.jpg",
        "assets/images/ilford-3.jpg",
        "assets/images/ilford-4.jpg",
      ],
      faqs: [
        {
          question: "Is there parking available?",
          answer: "Yes, ample parking available at the center.",
        },
        {
          question: "What subjects are covered in the mock exams?",
          answer:
            "For 11+, mocks usually include English, Mathematics, Verbal Reasoning, and Non-Verbal/Spatial Reasoning (depending on the exam board). GCSE mocks are offered in core subjects such as English, Mathematics, Computer Science, and Science, with additional subjects available at selected centres.",
        },
        {
          question: "Are the mock exams based on real exam formats?",
          answer:
            "Yes. All mock papers are designed to closely reflect the structure, timing, and difficulty level of the actual 11+ and GCSE examinations.",
        },
        {
          question: "Who writes the mock exam papers?",
          answer:
            "Papers are created by experienced subject specialists and reviewed to ensure accuracy, relevance, and appropriate exam standards.",
        },
        {
          question: "Are the mocks suitable for all ability levels?",
          answer:
            "Yes. The mock exams are designed to be accessible while still providing meaningful challenge and insight into a student’s current exam readiness.",
        },
        {
          question: "How do I book a mock exam?",
          answer:
            "Bookings can be made online via our website www.sehyog.co.uk. Places are limited and allocated on a first-come, first-served basis.",
        },
        {
          question: "Can I choose a specific exam centre?",
          answer:
            "Yes. Parents can select their preferred centre at the time of booking, subject to availability.",
        },
        {
          question: "What is included in the mock exam fee?",
          answer:
            "The fee includes the exam sitting, professional invigilation, marking, a detailed results report, and a doubt session for 11+ candidates only.",
        },
        {
          question: "Are refunds available if my child cannot attend?",
          answer:
            "Refund and rescheduling policies are clearly outlined during the booking process and vary depending on the notice given.",
        },
        {
          question: "What should students bring on the exam day?",
          answer:
            "Students should bring required stationery (pens, pencils, ruler, calculator where permitted) and their exam confirmation details.",
        },
        {
          question: "How closely do you follow real exam conditions?",
          answer:
            "Mock exams are conducted under strict exam conditions, including timed papers and formal invigilation, to help students build confidence and familiarity.",
        },
        {
          question: "How long do the exams last?",
          answer:
            "Exam durations match real exam timings and vary depending on the subject and level (11+ or GCSE).",
        },
        {
          question: "When will results be available?",
          answer:
            "Results are usually released within a specified timeframe after the exam (e.g. 3–5 days), depending on the exam type and number of students participating. In most cases, results are shared within 48 hours (T&C apply).",
        },
        {
          question: "What feedback will we receive?",
          answer:
            "Parents receive a detailed performance breakdown including scores, rank, percentile, average score, max/min score, strengths, areas for improvement, and overall exam readiness.",
        },
        {
          question: "Will rankings or comparisons be provided?",
          answer:
            "Where appropriate, results may include cohort comparisons or percentile rankings to give context to performance.",
        },
        {
          question:
            "Can the mock results be used to guide further preparation?",
          answer:
            "Absolutely. The results are designed to help identify gaps in knowledge and inform targeted revision or tuition plans.",
        },
        {
          question: "Do you offer follow-up support after the mocks?",
          answer:
            "Yes. Optional feedback sessions, revision workshops, or tuition may be available depending on the centre.",
        },
      ],

      schedules: [
        {
          title: "11+ CSSE Mock Tests",
          rows: [
            {
              test: "CSSE 1st SET",
              date: "Sunday, 15 March 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/bc1a321a2fdf36110e0d56a5d43846cd",
            },
            {
              test: "CSSE 2nd SET",
              date: "Sunday, 19 April 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/8091db5902670351bb31e0bc7c3bf45b",
            },
            {
              test: "CSSE 3rd SET",
              date: "Sunday, 10 May 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/9ed19dbf8b8f8354e6cbebaa862da05c",
            },
            {
              test: "CSSE 4th SET",
              date: "Sunday, 07 June 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/a3ad3b4531368b3186f4ff2ffd39d90d",
            },
            {
              test: "CSSE 5th SET",
              date: "Sunday, 28 June 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/b9fa6ab00ab5d55dfdfd639e0be6c2f6",
            },
            {
              test: "CSSE 6th SET",
              date: "Sunday, 05 July 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/15032d5564fd505bc688b741e3984688",
            },
            {
              test: "CSSE 7th SET",
              date: "Sunday, 19 July 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/11785b2ad68ccf2f4346d05ee2c8fae4",
            },
            {
              test: "CSSE 8th SET",
              date: "Sunday, 09 August 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/a66f8ed024a153985c146cc4cef988f3",
            },
            {
              test: "CSSE 9th SET",
              date: "Sunday, 16 August 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/392cc12734d771cc6d1e674b3108f100",
            },
            {
              test: "CSSE 10th SET",
              date: "Sunday, 30 August 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/a700dff03dc8003158f6648445f39f6d",
            },
            {
              test: "CSSE 11th SET",
              date: "Sunday, 13 September 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/3394f7dc535392b2cae079d8d0f68c26",
            },
          ],
        },
        {
          title: "11+ FSCE (CCHS) Mock Tests",
          rows: [
            {
              test: "FSCE 1st SET",
              date: "Sunday, 12 April 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/12fc0ced20b59522a3b150229a215b26",
            },
            {
              test: "FSCE 2nd SET",
              date: "Sunday, 03 May 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/413cb63be599a076d6a55b9fa9787665",
            },
            {
              test: "FSCE 3rd SET",
              date: "Sunday, 31 May 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/4d5acc7e34123665e8d358a8925e3d2b",
            },
            {
              test: "FSCE 4th SET",
              date: "Sunday, 21 June 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/a8163d0101c19ae35385483d32f04add",
            },
            {
              test: "FSCE 5th SET",
              date: "Sunday, 12 July 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/6ff8cee2a4dfefaade252cd114e92e61",
            },
            {
              test: "FSCE 6th SET",
              date: "Sunday, 02 August 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/08fb6768912358f5ebc120de5aee745a",
            },
            {
              test: "FSCE 7th SET",
              date: "Sunday, 23 August 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/5b87b70629ec5cc4f33193ebfe098a81",
            },
          ],
        },

        {
          title: "11+ GL Redbridge (GLRB) Mock Tests",
          rows: [
            {
              test: "GLRB 1st SET",
              date: "Sunday, 22 March 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/15cc9282a7d47a01b5838d45facc6727",
            },
            {
              test: "GLRB 2nd SET",
              date: "Sunday, 26 April 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/e9b37919bab36ed98d52996b0375e476",
            },
            {
              test: "GLRB 3rd SET",
              date: "Sunday, 17 May 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/7fc67f54e2a6d12bb32d32f8b4a723af",
            },
            {
              test: "GLRB 4th SET",
              date: "Sunday, 14 June 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/e70ebdd12a82e643ff0a2cb21a50532c",
            },
            {
              test: "GLRB 5th SET",
              date: "Sunday, 05 July 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/7260594657fc2cc76774b523fc088e10",
            },
            {
              test: "GLRB 6th SET",
              date: "Sunday, 26 July 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/280796e9fe69971a53a97adec50b7bf3",
            },
            {
              test: "GLRB 7th SET",
              date: "Sunday, 16 August 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/529e23984de28551cae64e474d7ce098",
            },
            {
              test: "GLRB 8th SET",
              date: "Sunday, 06 September 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/529e23984de28551cae64e474d7ce098",
            },
          ],
        },

        {
          title: "11+ GL QE Boys (GLQE) Mock Tests",
          rows: [
            {
              test: "GLQE 1st SET",
              date: "Sunday, 12 April 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/8240e0177a3b53531991204d215a8317",
            },
            {
              test: "GLQE 2nd SET",
              date: "Sunday, 23 May 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/a131cfe67082d4d37b4eefb4cf744683",
            },
            {
              test: "GLQE 3rd SET",
              date: "Sunday, 31 May 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/d51df8927cbbe5b47acfd274ddcc66e0",
            },
            {
              test: "GLQE 4th SET",
              date: "Sunday, 21 June 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/aacf93d885c25f8da9979ae5d5074158",
            },
            {
              test: "GLQE 5th SET",
              date: "Sunday, 12 July 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/06125fa6073842993943281063795024",
            },
            {
              test: "GLQE 6th SET",
              date: "Sunday, 02 August 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/e9ae155da2a981d6eb9aceaae2abf679",
            },
            {
              test: "GLQE 7th SET",
              date: "Sunday, 23 August 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/0edbd3d201454329b1d8ec1374786d81",
            },
          ],
        },

        {
          title: "11+ GL HBS Girls (GLHBS) Mock Tests",
          rows: [
            {
              test: "GLHBS 1st SET",
              date: "Sunday, 12 April 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/b483a2ec025cd24827af20cd73de1993",
            },
            {
              test: "GLHBS 2nd SET",
              date: "Sunday, 23 May 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/4fbcb9dc7700b34a1e9723a35c448f14",
            },
            {
              test: "GLHBS 3rd SET",
              date: "Sunday, 31 May 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/44cc5028ed6a2905db0a4091cbb03468",
            },
            {
              test: "GLHBS 4th SET",
              date: "Sunday, 21 June 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/e276235c58c50ccdaf536dff402cc7a7",
            },
            {
              test: "GLHBS 5th SET",
              date: "Sunday, 12 July 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/9e70910d583eab8ea4079c40283351fa",
            },
            {
              test: "GLHBS 6th SET",
              date: "Sunday, 02 August 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/9e875e02aa0e098047e8c3f0a85acb8d",
            },
            {
              test: "GLHBS 7th SET",
              date: "Sunday, 23 August 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/945c07ef61ed683d51f85241413c1abb",
            },
          ],
        },


        //Year 4
        {
          title: "11+ Year 4 Topic Tests",
          rows: [
            {
              test: "Maths Numbers & Factors (M01) & English Reading Comprehension (ECOMP01) & CW- Topic Tests ",
              date: "Sunday, March 15, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/649907768ca7acc158601bcbeacec279",
            },
            {
              test: "Maths Decimals (M02) & English Reading Comprehension (ECOMP02) & CW",
              date: "Sunday, March 22, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/bdb188ac01edc8aa991b8694121fadd4",
            },
            {
              test: "Maths Fractions (M03) & Verbal Non-Verbal Reasoning (VRNVR01) or CW01 - Topic Tests",
              date: "Sunday, April 12, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/1ba8f9ce03634fdd464343b8ac9bbf03",
            },
            {
              test: "Maths Percentage (M04) & English Reading Comprehension (ECOMP03) & CW",
              date: "Sunday, April 19, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/1e7122a65e862b62201ceaa90887060c",
            },
            {
              test: "Maths Money (M05) & English Reading Comprehension (ECOMP04)& CW",
              date: "Sunday, April 26, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/0d6631a308431aa98784232cbbacb36a",
            },
            {
              test: "Maths Data Handling (M06) & VR/NVR (VRNVR02) or CW02",
              date: "Sunday, May 3, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/5b9c3e808242fa4390eb5c95462bf4d7",
            },
            {
              test: "Maths Day Date Time (M07) & English Comprehension (ECOMP05)",
              date: "Sunday, May 10, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/e96c51ea1c19b3559f25b59a051671fa",
            },
//--------------------
            {
              test: "Maths Probability, Ratio and Proportion (M08) & Eng. Reading Comp. (ECOMP06) & CW",
              date: "Sunday, May 17, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/2907dd37923d217f9eec5bc9d5750652",
            },

            {
              test: "Maths Shapes, Area, Perimeter & Volume Part 01 (M09) & Eng Reading Comp (ECOMP07) & CW",
              date: "Sunday, May 31, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/d7cfb99cffa623a44b0ce6f4f6225d33",
            },


            {
              test: "Maths Algebra Part 01 (M10) & (CW03) & VRNVR03 - Topic Tests & CW",
              date: "Sunday, June 7, 2026",
              link: "",
            },
            {
              test: "Maths Sequence, Distance Speed Time, Temperature (M11) & English Reading Comprehension (ECOMP08) & CW",
              date: "Sunday, June 14, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/7189d60d6e52949b7d66152d350281b4",
            },
            {
              test: "Maths Shapes, Area, Perimeter & Volume Part#2 (M13) & English Reading Comprehension (ECOMP09)",
              date: "Sunday, June 28, 2026",
              link: "",
            },
            {
              test: "Maths Algebra Part 2 (M14) & 10th English Comprehension CSSE Style (ECOMP10)",
              date: "Sunday, July 5, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/74fae6ffc42dc00c66408f6bc9409d55",
            },

            {
              test: "Maths Algebra Part 2 (M14) & 10th English Comprehension CSSE Style (ECOMP10)",
              date: "Sunday, July 5, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/74fae6ffc42dc00c66408f6bc9409d55",
            },

            {
              test: "ALL Topics - Maths + Creative Writing05 or VRNVR05",
              date: "Sunday, July 12, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/77d094616906803abb6cde956359bdf0",
            },
          ],
        },
      ],

      mapUrl:
        "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2479.123!2d0.0878!3d51.5590!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47d8a00baf21de75%3A0x52963a5addd52a99!2sIlford%2C%20UK!5e0!3m2!1sen!2sus!4v1234567890",
    },

    //Basildon

    {
      id: "essex-basildon-11plus-mocks",
      town: "Basildon",
      address: "Basildon St Paul's Church SS15 5HL",
      postcode: "SS15 5HL",
      phone: "(0)2039808244",
      email: "support@sehyog.co.uk",
      image: "https://l5.tm-web-02.co.uk/lib/image-large-wp2-697855.webp",
      badge: "New",
      distance: 7.8,
      services: ["11+ Mock Exams", "GCSE Mock Exams"],
      description:
        "The Basildon exam centre is easily accessible and thoughtfully designed to promote concentration and confidence. We offer a supportive atmosphere where students of all ability levels can perform at their best.",
      openingHours: [
        { day: "Monday", time: "9:00 AM - 7:00 PM" },
        { day: "Tuesday", time: "9:00 AM - 7:00 PM" },
        { day: "Wednesday", time: "9:00 AM - 7:00 PM" },
        { day: "Thursday", time: "9:00 AM - 7:00 PM" },
        { day: "Friday", time: "9:00 AM - 7:00 PM" },
        { day: "Saturday", time: "9:00 AM - 7:00 PM" },
        { day: "Sunday", time: "9:00 AM - 7:00 PM" },
      ],
      photos: [
        "assets/images/romford-1.jpg",
        "assets/images/romford-2.jpg",
        "assets/images/romford-3.jpg",
        "assets/images/romford-4.jpg",
      ],
      faqs: [
        {
          question: "Is there parking available?",
          answer: "Yes, ample parking available at the center.",
        },
        {
          question: "What subjects are covered in the mock exams?",
          answer:
            "For 11+, mocks usually include English, Mathematics, Verbal Reasoning, and Non-Verbal/Spatial Reasoning (depending on the exam board). GCSE mocks are offered in core subjects such as English, Mathematics, Computer Science, and Science, with additional subjects available at selected centres.",
        },
        {
          question: "Are the mock exams based on real exam formats?",
          answer:
            "Yes. All mock papers are designed to closely reflect the structure, timing, and difficulty level of the actual 11+ and GCSE examinations.",
        },
        {
          question: "Who writes the mock exam papers?",
          answer:
            "Papers are created by experienced subject specialists and reviewed to ensure accuracy, relevance, and appropriate exam standards.",
        },
        {
          question: "Are the mocks suitable for all ability levels?",
          answer:
            "Yes. The mock exams are designed to be accessible while still providing meaningful challenge and insight into a student’s current exam readiness.",
        },
        {
          question: "How do I book a mock exam?",
          answer:
            "Bookings can be made online via our website www.sehyog.co.uk. Places are limited and allocated on a first-come, first-served basis.",
        },
        {
          question: "Can I choose a specific exam centre?",
          answer:
            "Yes. Parents can select their preferred centre at the time of booking, subject to availability.",
        },
        {
          question: "What is included in the mock exam fee?",
          answer:
            "The fee includes the exam sitting, professional invigilation, marking, a detailed results report, and a doubt session for 11+ candidates only.",
        },
        {
          question: "Are refunds available if my child cannot attend?",
          answer:
            "Refund and rescheduling policies are clearly outlined during the booking process and vary depending on the notice given.",
        },
        {
          question: "What should students bring on the exam day?",
          answer:
            "Students should bring required stationery (pens, pencils, ruler, calculator where permitted) and their exam confirmation details.",
        },
        {
          question: "How closely do you follow real exam conditions?",
          answer:
            "Mock exams are conducted under strict exam conditions, including timed papers and formal invigilation, to help students build confidence and familiarity.",
        },
        {
          question: "How long do the exams last?",
          answer:
            "Exam durations match real exam timings and vary depending on the subject and level (11+ or GCSE).",
        },
        {
          question: "When will results be available?",
          answer:
            "Results are usually released within a specified timeframe after the exam (e.g. 3–5 days), depending on the exam type and number of students participating. In most cases, results are shared within 48 hours (T&C apply).",
        },
        {
          question: "What feedback will we receive?",
          answer:
            "Parents receive a detailed performance breakdown including scores, rank, percentile, average score, max/min score, strengths, areas for improvement, and overall exam readiness.",
        },
        {
          question: "Will rankings or comparisons be provided?",
          answer:
            "Where appropriate, results may include cohort comparisons or percentile rankings to give context to performance.",
        },
        {
          question:
            "Can the mock results be used to guide further preparation?",
          answer:
            "Absolutely. The results are designed to help identify gaps in knowledge and inform targeted revision or tuition plans.",
        },
        {
          question: "Do you offer follow-up support after the mocks?",
          answer:
            "Yes. Optional feedback sessions, revision workshops, or tuition may be available depending on the centre.",
        },
      ],

      schedules: [
        {
          title: "11+ CSSE Mock Tests",
          rows: [
            {
              test: "CSSE 1st SET",
              date: "Sunday, 15 March 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/e33f72c7bacaeb0fd8286550c86b3dff",
            },
            {
              test: "CSSE 2nd SET",
              date: "Sunday, 19 April 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/251e349e4edd96d1aecdec7f8fdd69d5",
            },
            {
              test: "CSSE 3rd SET",
              date: "Sunday, 10 May 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/cca33ad4e71724ef003859ec7e2a05b0",
            },
            {
              test: "CSSE 4th SET",
              date: "Sunday, 07 June 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/da05f0872cd30afe9dc3bdf6fe650068",
            },
            {
              test: "CSSE 5th SET",
              date: "Sunday, 28 June 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/bc115d6cb328b45e79e489da7516fbe8",
            },
            {
              test: "CSSE 6th SET",
              date: "Sunday, 05 July 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/fff139c38066f1a1558c824e7d4a5f7e",
            },
            {
              test: "CSSE 7th SET",
              date: "Sunday, 19 July 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/61333551c44606065c673133d181f63a",
            },
            {
              test: "CSSE 8th SET",
              date: "Sunday, 09 August 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/c54d6ed8ab938689a5c3edeb699bd21a",
            },
            {
              test: "CSSE 9th SET",
              date: "Sunday, 16 August 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/5da6080aaf524b9312735ddb3fb4f920",
            },
            {
              test: "CSSE 10th SET",
              date: "Sunday, 30 August 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/f56d25bde70bdb27dda23cfd551f4ac3",
            },
            {
              test: "CSSE 11th SET",
              date: "Sunday, 13 September 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/562a69e30460205dede804d68978c76f",
            },
          ],
        },
        {
          title: "11+ FSCE Mock Tests (CCHS Girls)",
          rows: [
            {
              test: "FSCE 1st SET",
              date: "Sunday, 12 April 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/325fb66758cccfbc9da4862beff75b49",
            },
            {
              test: "FSCE 2nd SET",
              date: "Sunday, 03 May 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/8c716b83855757b0cd8fdd2ea1dbcb88",
            },
            {
              test: "FSCE 3rd SET",
              date: "Sunday, 31 May 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/68ce5ef0241b89b9b8e50a2c35f802ff",
            },
            {
              test: "FSCE 4th SET",
              date: "Sunday, 21 June 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/c4847f766fb38b978fb4913a3252a776",
            },
            {
              test: "FSCE 5th SET",
              date: "Sunday, 12 July 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/ed7b41726123a443b837765c8e01aba6",
            },
            {
              test: "FSCE 6th SET",
              date: "Sunday, 02 August 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/6fc0475873465022bae5538a01084eef",
            },
            {
              test: "FSCE 7th SET",
              date: "Sunday, 23 August 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/c9aeda00211d1b2aa66962a39bad15ff",
            },
          ],
        },

        //Year 4
        {
          title: "11+ Year 4 Topic Tests",
          rows: [
            {
              test: "Maths Numbers & Factors (M01) & English Reading Comprehension (ECOMP01) & CW- Topic Tests  ",
              date: "Sunday, March 15, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/4cce92a583c244c752022392a0fcdf4d",
            },
            {
              test: "Maths Fractions (M03) & Verbal Non-Verbal Reasoning (VRNVR01) or CW01 - Topic Tests",
              date: "Sunday, April 12, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/5694c94613c475e795608a9d34db8965",
            },
            {
              test: "Maths Percentage (M04) & English Reading Comprehension (ECOMP03) & CW",
              date: "Sunday, April 19, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/34a96d76690fcc3523d67120eb12672f",
            },
            {
              test: "Maths Data Handling (M06) & VR/NVR (VRNVR02) or CW02",
              date: "Sunday, May 3, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/92a34ae5c2dd28a7d4b37af3d49deaca",
            },
            {
              test: "Maths Day Date Time (M07) & English Comprehension (ECOMP05)",
              date: "Sunday, May 10, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/b00ea291639350070e0556f872b65e58",
            },
            {
              test: "Maths Shapes, Area, Perimeter & Volume Part 01 (M09) & Eng Reading Comp (ECOMP07) & CW",
              date: "Sunday, May 31, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/c40052299c828f5d687e4e93d5c78baa",
            },
            {
              test: "Maths Algebra Part 01 (M10) & (CW03) & VRNVR03 - Topic Tests & CW",
              date: "Sunday, June 7, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/9b9ce5e2ded2f773ee247ea3ab139316",
            },
//--------------------
            {
              test: "Maths Data Handling Part#2 (M12) & CW04 or VRNVR04",
              date: "Sunday, June 21, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/a72f19e45b5c36bb21e30aa3865e049c",
            },

            {
              test: "Maths Shapes, Area, Perimeter & Volume Part#2 (M13) & English Reading Comprehension (ECOMP09)",
              date: "Sunday, June 28, 2026",
              link: "",
            },


            {
              test: "Maths Algebra Part 2 (M14) & 10th English Comprehension CSSE Style (ECOMP10)",
              date: "Sunday, July 5, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/bd501fdebd2fdd19984c327475072501",
            },
            {
              test: "ALL Topics - Maths + Creative Writing05 or VRNVR05",
              date: "Sunday, July 12, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/6f9c58df74b6c31e18899df8f4cc6161",
            },
          ],
        },
      ],
      mapUrl:
        "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2478.456!2d0.1818!3d51.5762!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47d8a00baf21de75%3A0x52963a5addd52a99!2sRomford%2C%20UK!5e0!3m2!1sen!2sus!4v1234567890",
    },

    //South-end
    {
      id: "essex-southend-on-sea-11plus-mocks",
      town: "Southend-on-sea",
      address: "Thorpe Hall School, Wakering Rd, Southend-on-Sea ",
      postcode: "SS1 3RD",
      phone: "(0)2039808244",
      email: "support@sehyog.co.uk",
      image:
        "https://lh3.googleusercontent.com/p/AF1QipMZsUkgIylHxV-T7OALOq9JsFyYI4VLexJfeOBI=s1360-w1360-h1020",
      distance: 8.1,
      services: ["11+ Mock Exams", "GCSE Mock Exams"],
      description:
        "Our exam centre in Southend-on-sea combines convenient access with a welcoming, distraction-free environment. It is a professionally managed space that supports fair, focused, and comfortable examinations for every student.",
      openingHours: [
        { day: "Monday", time: "9:00 AM - 7:00 PM" },
        { day: "Tuesday", time: "9:00 AM - 7:00 PM" },
        { day: "Wednesday", time: "9:00 AM - 7:00 PM" },
        { day: "Thursday", time: "9:00 AM - 7:00 PM" },
        { day: "Friday", time: "9:00 AM - 6:00 PM" },
        { day: "Saturday", time: "9:00 AM - 7:00 PM" },
        { day: "Sunday", time: "9:00 AM - 7:00 PM" },
      ],
      photos: [
        "assets/images/brentwood-1.jpg",
        "assets/images/brentwood-2.jpg",
        "assets/images/brentwood-3.jpg",
        "assets/images/brentwood-4.jpg",
      ],
      faqs: [
        {
          question: "Is there parking available?",
          answer: "Yes, ample parking available at the center.",
        },
        {
          question: "What subjects are covered in the mock exams?",
          answer:
            "For 11+, mocks usually include English, Mathematics, Verbal Reasoning, and Non-Verbal/Spatial Reasoning (depending on the exam board). GCSE mocks are offered in core subjects such as English, Mathematics, Computer Science, and Science, with additional subjects available at selected centres.",
        },
        {
          question: "Are the mock exams based on real exam formats?",
          answer:
            "Yes. All mock papers are designed to closely reflect the structure, timing, and difficulty level of the actual 11+ and GCSE examinations.",
        },
        {
          question: "Who writes the mock exam papers?",
          answer:
            "Papers are created by experienced subject specialists and reviewed to ensure accuracy, relevance, and appropriate exam standards.",
        },
        {
          question: "Are the mocks suitable for all ability levels?",
          answer:
            "Yes. The mock exams are designed to be accessible while still providing meaningful challenge and insight into a student’s current exam readiness.",
        },
        {
          question: "How do I book a mock exam?",
          answer:
            "Bookings can be made online via our website www.sehyog.co.uk. Places are limited and allocated on a first-come, first-served basis.",
        },
        {
          question: "Can I choose a specific exam centre?",
          answer:
            "Yes. Parents can select their preferred centre at the time of booking, subject to availability.",
        },
        {
          question: "What is included in the mock exam fee?",
          answer:
            "The fee includes the exam sitting, professional invigilation, marking, a detailed results report, and a doubt session for 11+ candidates only.",
        },
        {
          question: "Are refunds available if my child cannot attend?",
          answer:
            "Refund and rescheduling policies are clearly outlined during the booking process and vary depending on the notice given.",
        },
        {
          question: "What should students bring on the exam day?",
          answer:
            "Students should bring required stationery (pens, pencils, ruler, calculator where permitted) and their exam confirmation details.",
        },
        {
          question: "How closely do you follow real exam conditions?",
          answer:
            "Mock exams are conducted under strict exam conditions, including timed papers and formal invigilation, to help students build confidence and familiarity.",
        },
        {
          question: "How long do the exams last?",
          answer:
            "Exam durations match real exam timings and vary depending on the subject and level (11+ or GCSE).",
        },
        {
          question: "When will results be available?",
          answer:
            "Results are usually released within a specified timeframe after the exam (e.g. 3–5 days), depending on the exam type and number of students participating. In most cases, results are shared within 48 hours (T&C apply).",
        },
        {
          question: "What feedback will we receive?",
          answer:
            "Parents receive a detailed performance breakdown including scores, rank, percentile, average score, max/min score, strengths, areas for improvement, and overall exam readiness.",
        },
        {
          question: "Will rankings or comparisons be provided?",
          answer:
            "Where appropriate, results may include cohort comparisons or percentile rankings to give context to performance.",
        },
        {
          question:
            "Can the mock results be used to guide further preparation?",
          answer:
            "Absolutely. The results are designed to help identify gaps in knowledge and inform targeted revision or tuition plans.",
        },
        {
          question: "Do you offer follow-up support after the mocks?",
          answer:
            "Yes. Optional feedback sessions, revision workshops, or tuition may be available depending on the centre.",
        },
      ],

      schedules: [
        {
          title: "CSSE Mock Tests",
          rows: [
            {
              test: "CSSE 1st SET",
              date: "Sunday, 15 March 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/7235e9c28bbd61e9a451b6b4f7875ebb",
            },
            {
              test: "CSSE 2nd SET",
              date: "Sunday, 19 April 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/77886e803b49ac69549a6813114784aa",
            },
            {
              test: "CSSE 3rd SET",
              date: "Sunday, 10 May 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/da2475a18436fc27f13a2d8e27c6b215",
            },
            {
              test: "CSSE 4th SET",
              date: "Sunday, 07 June 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/dbb8fa30bca121ada36ef342c4d53225",
            },
            {
              test: "CSSE 5th SET",
              date: "Sunday, 28 June 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/d7be8e84919ef2b02c127c6d34ff322f",
            },
            {
              test: "CSSE 6th SET",
              date: "Sunday, 05 July 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/64dda2cf498392af7cfd9de6bc14aa4e",
            },
            {
              test: "CSSE 7th SET",
              date: "Sunday, 19 July 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/afa0a0caaf61347abfa55a38fe3893f1",
            },
            {
              test: "CSSE 8th SET",
              date: "Sunday, 09 August 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/afa0a0caaf61347abfa55a38fe3893f1",
            },
            {
              test: "CSSE 9th SET",
              date: "Sunday, 16 August 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/88f4af952d4d8eb38941fc87ed9d591f",
            },
            {
              test: "CSSE 10th SET",
              date: "Sunday, 30 August 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/88f4af952d4d8eb38941fc87ed9d591f",
            },
            {
              test: "CSSE 11th SET",
              date: "Sunday, 13 September 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/8569c22ee67ba70b272fb2e0550b902f",
            },
          ],
        },

        //Year 4
        {
          title: "11+ Year 4 Topic Tests",
          rows: [
            {
              test: "Maths Numbers & Factors (M01) & English Reading Comprehension (ECOMP01) & CW- Topic Tests ",
              date: "Sunday, March 15, 2026",
              link: "",
            },
            {
              test: "Maths Percentage (M04) & English Reading Comprehension (ECOMP03) & CW",
              date: "Sunday, April 19, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/d45633e9e6a70b1119f55d60557dc457",
            },
            {
              test: "Maths Day Date Time (M07) & English Comprehension (ECOMP05)",
              date: "Sunday, May 10, 2026",
              link: "",
            },
            {
              test: "Maths Shapes, Area, Perimeter & Volume Part#2 (M13) & English Reading Comprehension (ECOMP09)",
              date: "Sunday, June 28, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/32c48469f2967918c96afc92b19ce426",
            },
            {
              test: "Maths Algebra Part 2 (M14) & 10th English Comprehension CSSE Style (ECOMP10)",
              date: "Sunday, July 5, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/d45633e9e6a70b1119f55d60557dc457",
            },
            
          ],
        },
      ],
      mapUrl:
        "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2478.789!2d0.3045!3d51.6217!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47d8a00baf21de75%3A0x52963a5addd52a99!2sBrentwood%2C%20UK!5e0!3m2!1sen!2sus!4v1234567890",
    },

    //Bexley

    {
      id: "kent-bexley-11plus-mocks",
      town: "Bexleyheath",
      address: " ",
      postcode: "SS1 3RD",
      phone: "(0)2039808244",
      email: "support@sehyog.co.uk",
      image:
        "https://images.squarespace-cdn.com/content/v1/56520f11e4b02e2458a7dff8/890ce7a4-4101-433e-ade1-78b975d07d61/Beths-Grammar-School-11-plus-Exams-London%2B.jpg",
      distance: 8.1,
      services: ["11+ Mock Exams", "GCSE Mock Exams"],
      description:
        "Our exam centre in Southend-on-sea combines convenient access with a welcoming, distraction-free environment. It is a professionally managed space that supports fair, focused, and comfortable examinations for every student.",
      openingHours: [
        { day: "Monday", time: "9:00 AM - 7:00 PM" },
        { day: "Tuesday", time: "9:00 AM - 7:00 PM" },
        { day: "Wednesday", time: "9:00 AM - 7:00 PM" },
        { day: "Thursday", time: "9:00 AM - 7:00 PM" },
        { day: "Friday", time: "9:00 AM - 6:00 PM" },
        { day: "Saturday", time: "9:00 AM - 7:00 PM" },
        { day: "Sunday", time: "9:00 AM - 7:00 PM" },
      ],
      photos: [
        "assets/images/brentwood-1.jpg",
        "assets/images/brentwood-2.jpg",
        "assets/images/brentwood-3.jpg",
        "assets/images/brentwood-4.jpg",
      ],
      faqs: [
        {
          question: "Is there parking available?",
          answer: "Yes, ample parking available at the center.",
        },
        {
          question: "What subjects are covered in the mock exams?",
          answer:
            "For 11+, mocks usually include English, Mathematics, Verbal Reasoning, and Non-Verbal/Spatial Reasoning (depending on the exam board). GCSE mocks are offered in core subjects such as English, Mathematics, Computer Science, and Science, with additional subjects available at selected centres.",
        },
        {
          question: "Are the mock exams based on real exam formats?",
          answer:
            "Yes. All mock papers are designed to closely reflect the structure, timing, and difficulty level of the actual 11+ and GCSE examinations.",
        },
        {
          question: "Who writes the mock exam papers?",
          answer:
            "Papers are created by experienced subject specialists and reviewed to ensure accuracy, relevance, and appropriate exam standards.",
        },
        {
          question: "Are the mocks suitable for all ability levels?",
          answer:
            "Yes. The mock exams are designed to be accessible while still providing meaningful challenge and insight into a student’s current exam readiness.",
        },
        {
          question: "How do I book a mock exam?",
          answer:
            "Bookings can be made online via our website www.sehyog.co.uk. Places are limited and allocated on a first-come, first-served basis.",
        },
        {
          question: "Can I choose a specific exam centre?",
          answer:
            "Yes. Parents can select their preferred centre at the time of booking, subject to availability.",
        },
        {
          question: "What is included in the mock exam fee?",
          answer:
            "The fee includes the exam sitting, professional invigilation, marking, a detailed results report, and a doubt session for 11+ candidates only.",
        },
        {
          question: "Are refunds available if my child cannot attend?",
          answer:
            "Refund and rescheduling policies are clearly outlined during the booking process and vary depending on the notice given.",
        },
        {
          question: "What should students bring on the exam day?",
          answer:
            "Students should bring required stationery (pens, pencils, ruler, calculator where permitted) and their exam confirmation details.",
        },
        {
          question: "How closely do you follow real exam conditions?",
          answer:
            "Mock exams are conducted under strict exam conditions, including timed papers and formal invigilation, to help students build confidence and familiarity.",
        },
        {
          question: "How long do the exams last?",
          answer:
            "Exam durations match real exam timings and vary depending on the subject and level (11+ or GCSE).",
        },
        {
          question: "When will results be available?",
          answer:
            "Results are usually released within a specified timeframe after the exam (e.g. 3–5 days), depending on the exam type and number of students participating. In most cases, results are shared within 48 hours (T&C apply).",
        },
        {
          question: "What feedback will we receive?",
          answer:
            "Parents receive a detailed performance breakdown including scores, rank, percentile, average score, max/min score, strengths, areas for improvement, and overall exam readiness.",
        },
        {
          question: "Will rankings or comparisons be provided?",
          answer:
            "Where appropriate, results may include cohort comparisons or percentile rankings to give context to performance.",
        },
        {
          question:
            "Can the mock results be used to guide further preparation?",
          answer:
            "Absolutely. The results are designed to help identify gaps in knowledge and inform targeted revision or tuition plans.",
        },
        {
          question: "Do you offer follow-up support after the mocks?",
          answer:
            "Yes. Optional feedback sessions, revision workshops, or tuition may be available depending on the centre.",
        },
      ],

      schedules: [
        {
          title: "11+ GL Kent Mock Tests – Bexleyheath",
          rows: [
            {
              test: "GL Kent 1st SET",
              date: "Sunday, April 12, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/b75c304730c55341bae19b5d9f2c9ca8",
            },
            {
              test: "GL Kent 2nd",
              date: "Sunday, May 03, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/8d89bb42a8db4b1bda692f9c6731d90f",
            },
            {
              test: "GL Kent 3rd",
              date: "Sunday, May 31, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/7b116dfa739ebd142d814eb8830fd5f9",
            },
            {
              test: "GL Kent 4th",
              date: "Sunday, June 21, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/b88941b24ee078884a2115d4764b8dd5",
            },
            {
              test: "GL Kent 5th",
              date: "Sunday, July 12, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/d719b80d8f1fa84f93c0cb4bcb72ca12",
            },
            {
              test: "GL Kent 6th",
              date: "Sunday, August 02, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/235d15b4f65fb1971b4c7bce48bc13d7",
            },
            {
              test: "GL Kent 7th",
              date: "Sunday, August 23, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/6ef9d4923773176ea8cb5862290eb205",
            },
          ],
        },

        {
          title: "11+ GL Bexley Mock Tests – Bexleyheath",
          rows: [
            {
              test: "GL Bexley 1st SET",
              date: "Sunday, March 22, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/05d66cf3937f1b38c8c4da7258737135",
            },
            {
              test: "GL Bexley 2nd",
              date: "Sunday, April 26, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/d1b234a7d1dc20f6993a905a86caf2e6",
            },
            {
              test: "GL Bexley 3rd",
              date: "Sunday, May 17, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/0c2e40d7266d4b00c6a3e6b58ac073e5",
            },
            {
              test: "GL Bexley 4th",
              date: "Sunday, June 14, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/a25bb410c31bc2f1a8404a9192dad0f2",
            },
            {
              test: "GL Bexley 5th",
              date: "Sunday, July 05, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/11137da0bc68093d89620ab8f48ae221",
            },
            {
              test: "GL Bexley 6th",
              date: "Sunday, July 26, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/bea360a4b2780570c3292f5399bbd578",
            },
            {
              test: "GL Bexley 7th",
              date: "Sunday, August 16, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/972c631a5458b1a83b32bf1bcf163ce7",
            },
          ],
        },
      ],
      mapUrl:
        "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2478.789!2d0.3045!3d51.6217!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47d8a00baf21de75%3A0x52963a5addd52a99!2sBrentwood%2C%20UK!5e0!3m2!1sen!2sus!4v1234567890",
    },

    //Amersham

    {
      id: "bucks-amersham-11plus-mocks",
      town: "Amersham",
      address: " ",
      postcode: "SS1 3RD",
      phone: "(0)2039808244",
      email: "support@sehyog.co.uk",
      image: "https://school-space-documents.s3.amazonaws.com/IMG_1098.JPG",
      distance: 8.1,
      services: ["11+ Mock Exams", "GCSE Mock Exams"],
      description:
        "Our exam centre in Southend-on-sea combines convenient access with a welcoming, distraction-free environment. It is a professionally managed space that supports fair, focused, and comfortable examinations for every student.",
      openingHours: [
        { day: "Monday", time: "9:00 AM - 7:00 PM" },
        { day: "Tuesday", time: "9:00 AM - 7:00 PM" },
        { day: "Wednesday", time: "9:00 AM - 7:00 PM" },
        { day: "Thursday", time: "9:00 AM - 7:00 PM" },
        { day: "Friday", time: "9:00 AM - 6:00 PM" },
        { day: "Saturday", time: "9:00 AM - 7:00 PM" },
        { day: "Sunday", time: "9:00 AM - 7:00 PM" },
      ],
      photos: [
        "assets/images/brentwood-1.jpg",
        "assets/images/brentwood-2.jpg",
        "assets/images/brentwood-3.jpg",
        "assets/images/brentwood-4.jpg",
      ],

      faqs: [
        {
          question: "Is there parking available?",
          answer: "Yes, ample parking available at the center.",
        },
        {
          question: "What subjects are covered in the mock exams?",
          answer:
            "For 11+, mocks usually include English, Mathematics, Verbal Reasoning, and Non-Verbal/Spatial Reasoning (depending on the exam board). GCSE mocks are offered in core subjects such as English, Mathematics, Computer Science, and Science, with additional subjects available at selected centres.",
        },
        {
          question: "Are the mock exams based on real exam formats?",
          answer:
            "Yes. All mock papers are designed to closely reflect the structure, timing, and difficulty level of the actual 11+ and GCSE examinations.",
        },
        {
          question: "Who writes the mock exam papers?",
          answer:
            "Papers are created by experienced subject specialists and reviewed to ensure accuracy, relevance, and appropriate exam standards.",
        },
        {
          question: "Are the mocks suitable for all ability levels?",
          answer:
            "Yes. The mock exams are designed to be accessible while still providing meaningful challenge and insight into a student’s current exam readiness.",
        },
        {
          question: "How do I book a mock exam?",
          answer:
            "Bookings can be made online via our website www.sehyog.co.uk. Places are limited and allocated on a first-come, first-served basis.",
        },
        {
          question: "Can I choose a specific exam centre?",
          answer:
            "Yes. Parents can select their preferred centre at the time of booking, subject to availability.",
        },
        {
          question: "What is included in the mock exam fee?",
          answer:
            "The fee includes the exam sitting, professional invigilation, marking, a detailed results report, and a doubt session for 11+ candidates only.",
        },
        {
          question: "Are refunds available if my child cannot attend?",
          answer:
            "Refund and rescheduling policies are clearly outlined during the booking process and vary depending on the notice given.",
        },
        {
          question: "What should students bring on the exam day?",
          answer:
            "Students should bring required stationery (pens, pencils, ruler, calculator where permitted) and their exam confirmation details.",
        },
        {
          question: "How closely do you follow real exam conditions?",
          answer:
            "Mock exams are conducted under strict exam conditions, including timed papers and formal invigilation, to help students build confidence and familiarity.",
        },
        {
          question: "How long do the exams last?",
          answer:
            "Exam durations match real exam timings and vary depending on the subject and level (11+ or GCSE).",
        },
        {
          question: "When will results be available?",
          answer:
            "Results are usually released within a specified timeframe after the exam (e.g. 3–5 days), depending on the exam type and number of students participating. In most cases, results are shared within 48 hours (T&C apply).",
        },
        {
          question: "What feedback will we receive?",
          answer:
            "Parents receive a detailed performance breakdown including scores, rank, percentile, average score, max/min score, strengths, areas for improvement, and overall exam readiness.",
        },
        {
          question: "Will rankings or comparisons be provided?",
          answer:
            "Where appropriate, results may include cohort comparisons or percentile rankings to give context to performance.",
        },
        {
          question:
            "Can the mock results be used to guide further preparation?",
          answer:
            "Absolutely. The results are designed to help identify gaps in knowledge and inform targeted revision or tuition plans.",
        },
        {
          question: "Do you offer follow-up support after the mocks?",
          answer:
            "Yes. Optional feedback sessions, revision workshops, or tuition may be available depending on the centre.",
        },
      ],

      schedules: [
        {
          title: "11+ Mock Tests – Amersham",
          rows: [
            {
              test: "GL Bucks 1st SET",
              date: "Sunday, March 22, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/bbbe4bc9de0077fcebf1404b18d1ffa4",
            },
            {
              test: "GL Bucks 2nd SET",
              date: "Sunday, April 26, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/40e3da774b9a558165b4366ad9f8abed",
            },
            {
              test: "GL Bucks 3rd SET",
              date: "Sunday, May 17, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/00b01254fe16e2b4a6e09d0e497fa47f",
            },
            {
              test: "GL Bucks 4th SET",
              date: "Sunday, June 14, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/c6919d02a8f0a3ef42d2c8ae82d114a3",
            },
            {
              test: "GL Bucks 5th SET",
              date: "Sunday, July 05, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/b9081dc92f2ce32a8648516067a9623b",
            },
            {
              test: "GL Bucks 6th SET",
              date: "Sunday, July 26, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/926999220216d17d658db20294a983f1",
            },
            {
              test: "GL Bucks 7th SET",
              date: "Sunday, August 16, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/a17fa69ade27b8d7679b5d095250de54",
            },
          ],
        },

        //Year 4
        {
          title: "11+ Year 4 Topic Tests",
          rows: [
            {
              test: "Maths Numbers & Factors (M01) & English Reading Comprehension (ECOMP01) & CW- Topic Tests ",
              date: "Sunday, March 15, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/eba525eaaf744f5900ba8bbd9a611c8c",
            },
            {
              test: "Maths Decimals (M02) & English Reading Comprehension (ECOMP02) & CW",
              date: "Sunday, March 22, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/c94cacce967a0ff2e451e4ae6376c86a",
            },
            {
              test: "Maths Fractions (M03) & Verbal Non-Verbal Reasoning (VRNVR01) or CW01 - Topic Tests",
              date: "Sunday, April 12, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/666e476f9d6d921ba052addfb5ac79f0",
            },
            {
              test: "Maths Percentage (M04) & English Reading Comprehension (ECOMP03) & CW",
              date: "Sunday, April 19, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/f5215eb9c14f42162bac6dae1f1c036f",
            },
            {
              test: "Maths Money (M05) & English Reading Comprehension (ECOMP04)& CW",
              date: "Sunday, April 26, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/79564bee9c5a7aa62f18c4ba45507ce6",
            },
//...................................
            {
              test: "Maths Probability, Ratio and Proportion (M08) & Eng. Reading Comp. (ECOMP06) & CW",
              date: "Sunday, May 17, 2026",
              link: "",
            },

            {
              test: "Maths Sequence, Distance Speed Time, Temperature (M11) & English Reading Comprehension (ECOMP08) & CW",
              date: "Sunday, June 14, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/8d3ca2dd4e9f758a67d0f1dce0b362b3",
            },

            {
              test: "Maths Algebra Part 2 (M14) & 10th English Comprehension CSSE Style (ECOMP10)",
              date: "Sunday, July 5, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/y4_11plus_topic_test/b12f29b6f5c52972768e38e85d655f5d",
            },
            
          ],
        },
      ],
      mapUrl:
        "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2478.789!2d0.3045!3d51.6217!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47d8a00baf21de75%3A0x52963a5addd52a99!2sBrentwood%2C%20UK!5e0!3m2!1sen!2sus!4v1234567890",
    },

    //Milton Keynes

    {
      id: "bucks-milton-keynes-11plus-mocks",
      town: "Milton Keynes",
      address: " ",
      postcode: "SS1 3RD",
      phone: "(0)2039808244",
      email: "support@sehyog.co.uk",
      image:
        "https://upload.wikimedia.org/wikipedia/commons/0/07/St_Pauls_School_Milton_Keynes.jpg",
      distance: 8.1,
      services: ["11+ Mock Exams", "GCSE Mock Exams"],
      description:
        "Our exam centre in Southend-on-sea combines convenient access with a welcoming, distraction-free environment. It is a professionally managed space that supports fair, focused, and comfortable examinations for every student.",
      openingHours: [
        { day: "Monday", time: "9:00 AM - 7:00 PM" },
        { day: "Tuesday", time: "9:00 AM - 7:00 PM" },
        { day: "Wednesday", time: "9:00 AM - 7:00 PM" },
        { day: "Thursday", time: "9:00 AM - 7:00 PM" },
        { day: "Friday", time: "9:00 AM - 6:00 PM" },
        { day: "Saturday", time: "9:00 AM - 7:00 PM" },
        { day: "Sunday", time: "9:00 AM - 7:00 PM" },
      ],
      photos: [
        "assets/images/brentwood-1.jpg",
        "assets/images/brentwood-2.jpg",
        "assets/images/brentwood-3.jpg",
        "assets/images/brentwood-4.jpg",
      ],

      faqs: [
        {
          question: "Is there parking available?",
          answer: "Yes, ample parking available at the center.",
        },
        {
          question: "What subjects are covered in the mock exams?",
          answer:
            "For 11+, mocks usually include English, Mathematics, Verbal Reasoning, and Non-Verbal/Spatial Reasoning (depending on the exam board). GCSE mocks are offered in core subjects such as English, Mathematics, Computer Science, and Science, with additional subjects available at selected centres.",
        },
        {
          question: "Are the mock exams based on real exam formats?",
          answer:
            "Yes. All mock papers are designed to closely reflect the structure, timing, and difficulty level of the actual 11+ and GCSE examinations.",
        },
        {
          question: "Who writes the mock exam papers?",
          answer:
            "Papers are created by experienced subject specialists and reviewed to ensure accuracy, relevance, and appropriate exam standards.",
        },
        {
          question: "Are the mocks suitable for all ability levels?",
          answer:
            "Yes. The mock exams are designed to be accessible while still providing meaningful challenge and insight into a student’s current exam readiness.",
        },
        {
          question: "How do I book a mock exam?",
          answer:
            "Bookings can be made online via our website www.sehyog.co.uk. Places are limited and allocated on a first-come, first-served basis.",
        },
        {
          question: "Can I choose a specific exam centre?",
          answer:
            "Yes. Parents can select their preferred centre at the time of booking, subject to availability.",
        },
        {
          question: "What is included in the mock exam fee?",
          answer:
            "The fee includes the exam sitting, professional invigilation, marking, a detailed results report, and a doubt session for 11+ candidates only.",
        },
        {
          question: "Are refunds available if my child cannot attend?",
          answer:
            "Refund and rescheduling policies are clearly outlined during the booking process and vary depending on the notice given.",
        },
        {
          question: "What should students bring on the exam day?",
          answer:
            "Students should bring required stationery (pens, pencils, ruler, calculator where permitted) and their exam confirmation details.",
        },
        {
          question: "How closely do you follow real exam conditions?",
          answer:
            "Mock exams are conducted under strict exam conditions, including timed papers and formal invigilation, to help students build confidence and familiarity.",
        },
        {
          question: "How long do the exams last?",
          answer:
            "Exam durations match real exam timings and vary depending on the subject and level (11+ or GCSE).",
        },
        {
          question: "When will results be available?",
          answer:
            "Results are usually released within a specified timeframe after the exam (e.g. 3–5 days), depending on the exam type and number of students participating. In most cases, results are shared within 48 hours (T&C apply).",
        },
        {
          question: "What feedback will we receive?",
          answer:
            "Parents receive a detailed performance breakdown including scores, rank, percentile, average score, max/min score, strengths, areas for improvement, and overall exam readiness.",
        },
        {
          question: "Will rankings or comparisons be provided?",
          answer:
            "Where appropriate, results may include cohort comparisons or percentile rankings to give context to performance.",
        },
        {
          question:
            "Can the mock results be used to guide further preparation?",
          answer:
            "Absolutely. The results are designed to help identify gaps in knowledge and inform targeted revision or tuition plans.",
        },
        {
          question: "Do you offer follow-up support after the mocks?",
          answer:
            "Yes. Optional feedback sessions, revision workshops, or tuition may be available depending on the centre.",
        },
      ],

      schedules: [
        {
          title: "11+ Mock Tests – Amersham",
          rows: [
            {
              test: "GL Bucks 1st SET",
              date: "Saturday, March 21, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/c359636be1c830e6042a9fa0b4db633f",
            },
            {
              test: "GL Bucks 2nd SET",
              date: "Saturday, April 25, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/ac71388632f172cb1d6075eaceca2fa7",
            },
            {
              test: "GL Bucks 3rd SET",
              date: "Saturday, May 16, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/6112a8375c338d53d65874af0527d6df",
            },
            {
              test: "GL Bucks 4th SET",
              date: "Saturday, June 13, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/664c9e918cd16d17acca40840669b77b",
            },
            {
              test: "GL Bucks 5th SET",
              date: "Saturday, July 03, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/c12796bf12b1056ea39d72f23b1a03ca",
            },
            {
              test: "GL Bucks 6th SET",
              date: "Saturday, July 25, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/f40e0f6b3e03a65eeca966030aa8649b",
            },
            {
              test: "GL Bucks 7th SET",
              date: "Saturday, August 15, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/7d1d9698c737bf4ad94505eafce0a132",
            },
          ],
        },
      ],
      mapUrl:
        "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2478.789!2d0.3045!3d51.6217!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47d8a00baf21de75%3A0x52963a5addd52a99!2sBrentwood%2C%20UK!5e0!3m2!1sen!2sus!4v1234567890",
    },

    //Harrow

    {
      id: "harrow-11plus-mocks",
      town: "Harrow",
      address: " ",
      postcode: "SS1 3RD",
      phone: "(0)2039808244",
      email: "support@sehyog.co.uk",
      image:
        "https://www.academic-asia.com/_next/image?url=https%3A%2F%2Facademic-asia.s3.ap-east-1.amazonaws.com%2Fcentral_22_without_roadworks2_2_04525644b2.jpg&w=3840&q=100",
      distance: 8.1,
      services: ["11+ Mock Exams", "GCSE Mock Exams"],
      description:
        "Our exam centre in Southend-on-sea combines convenient access with a welcoming, distraction-free environment. It is a professionally managed space that supports fair, focused, and comfortable examinations for every student.",
      openingHours: [
        { day: "Monday", time: "9:00 AM - 7:00 PM" },
        { day: "Tuesday", time: "9:00 AM - 7:00 PM" },
        { day: "Wednesday", time: "9:00 AM - 7:00 PM" },
        { day: "Thursday", time: "9:00 AM - 7:00 PM" },
        { day: "Friday", time: "9:00 AM - 6:00 PM" },
        { day: "Saturday", time: "9:00 AM - 7:00 PM" },
        { day: "Sunday", time: "9:00 AM - 7:00 PM" },
      ],
      photos: [
        "assets/images/brentwood-1.jpg",
        "assets/images/brentwood-2.jpg",
        "assets/images/brentwood-3.jpg",
        "assets/images/brentwood-4.jpg",
      ],

      faqs: [
        {
          question: "Is there parking available?",
          answer: "Yes, ample parking available at the center.",
        },
        {
          question: "What subjects are covered in the mock exams?",
          answer:
            "For 11+, mocks usually include English, Mathematics, Verbal Reasoning, and Non-Verbal/Spatial Reasoning (depending on the exam board). GCSE mocks are offered in core subjects such as English, Mathematics, Computer Science, and Science, with additional subjects available at selected centres.",
        },
        {
          question: "Are the mock exams based on real exam formats?",
          answer:
            "Yes. All mock papers are designed to closely reflect the structure, timing, and difficulty level of the actual 11+ and GCSE examinations.",
        },
        {
          question: "Who writes the mock exam papers?",
          answer:
            "Papers are created by experienced subject specialists and reviewed to ensure accuracy, relevance, and appropriate exam standards.",
        },
        {
          question: "Are the mocks suitable for all ability levels?",
          answer:
            "Yes. The mock exams are designed to be accessible while still providing meaningful challenge and insight into a student’s current exam readiness.",
        },
        {
          question: "How do I book a mock exam?",
          answer:
            "Bookings can be made online via our website www.sehyog.co.uk. Places are limited and allocated on a first-come, first-served basis.",
        },
        {
          question: "Can I choose a specific exam centre?",
          answer:
            "Yes. Parents can select their preferred centre at the time of booking, subject to availability.",
        },
        {
          question: "What is included in the mock exam fee?",
          answer:
            "The fee includes the exam sitting, professional invigilation, marking, a detailed results report, and a doubt session for 11+ candidates only.",
        },
        {
          question: "Are refunds available if my child cannot attend?",
          answer:
            "Refund and rescheduling policies are clearly outlined during the booking process and vary depending on the notice given.",
        },
        {
          question: "What should students bring on the exam day?",
          answer:
            "Students should bring required stationery (pens, pencils, ruler, calculator where permitted) and their exam confirmation details.",
        },
        {
          question: "How closely do you follow real exam conditions?",
          answer:
            "Mock exams are conducted under strict exam conditions, including timed papers and formal invigilation, to help students build confidence and familiarity.",
        },
        {
          question: "How long do the exams last?",
          answer:
            "Exam durations match real exam timings and vary depending on the subject and level (11+ or GCSE).",
        },
        {
          question: "When will results be available?",
          answer:
            "Results are usually released within a specified timeframe after the exam (e.g. 3–5 days), depending on the exam type and number of students participating. In most cases, results are shared within 48 hours (T&C apply).",
        },
        {
          question: "What feedback will we receive?",
          answer:
            "Parents receive a detailed performance breakdown including scores, rank, percentile, average score, max/min score, strengths, areas for improvement, and overall exam readiness.",
        },
        {
          question: "Will rankings or comparisons be provided?",
          answer:
            "Where appropriate, results may include cohort comparisons or percentile rankings to give context to performance.",
        },
        {
          question:
            "Can the mock results be used to guide further preparation?",
          answer:
            "Absolutely. The results are designed to help identify gaps in knowledge and inform targeted revision or tuition plans.",
        },
        {
          question: "Do you offer follow-up support after the mocks?",
          answer:
            "Yes. Optional feedback sessions, revision workshops, or tuition may be available depending on the centre.",
        },
      ],

      schedules: [
        {
          title: "11+ GL QE Boys Mock Tests – Harrow",
          rows: [
            {
              test: "GL QE Boys 1st SET",
              date: "Sunday, March 12, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/4b6d66128f00532dcfbc7680b6545d41",
            },
            {
              test: "GL QE Boys 2nd SET",
              date: "Sunday, April 03, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/f94c17ed712de72e3936df97c9a43075",
            },
            {
              test: "GL QE Boys 3rd SET",
              date: "Sunday, April 31, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/9e201c2cbfe3b627579096e6d15ab91b",
            },
            {
              test: "GL QE Boys 4th SET",
              date: "Sunday, May 21, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/e4d647aad1b83b80c0053d3e9df0d96e",
            },
            {
              test: "GL QE Boys 5th SET",
              date: "Sunday, June 12, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/d98722f59e0da089a7b46a5f05582a25",
            },
            {
              test: "GL QE Boys 6th SET",
              date: "Sunday, July 02, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/e301444ef3744d3166a89cff3efa8325",
            },
            {
              test: "GL QE Boys 7th SET",
              date: "Sunday, July 23, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/80a21ef8f45961e0caba1363eca7be89",
            },
          ],
        },

        {
          title: "11+ GL HBS Girls Mock Tests – Harrow",
          rows: [
            {
              test: "GL HBS Girls 1st SET",
              date: "Sunday, March 12, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/3b2e41c8a5c2912baf0d1c31bbca6b1f",
            },
            {
              test: "GL HBS Girls 2nd SET",
              date: "Sunday, April 03, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/80a60c4a218a10a1fc98f22b8c0525d6",
            },
            {
              test: "GL HBS Girls 3rd SET",
              date: "Sunday, April 31, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/f3d0ad93c3c01957444b36e0293c928b",
            },
            {
              test: "GL HBS Girls 4th SET",
              date: "Sunday, May 21, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/686a455bb023ad285268c81f2ff798c0",
            },
            {
              test: "GL HBS Girls 5th SET",
              date: "Sunday, June 12, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/f860f43ffd69bd6f51c686d9482c2aa7",
            },
            {
              test: "GL HBS Girls 6th SET",
              date: "Sunday, July 02, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/6d71f628b99a092dba24820827d1e64d",
            },
            {
              test: "GL HBS Girls 7th SET",
              date: "Sunday, July 23, 2026",
              link: "https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/9798c7a68e5c84cbcb7d90bf86d0fb65",
            },
          ],
        },
      ],
      mapUrl:
        "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2478.789!2d0.3045!3d51.6217!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47d8a00baf21de75%3A0x52963a5addd52a99!2sBrentwood%2C%20UK!5e0!3m2!1sen!2sus!4v1234567890",
    },

    {
      id: "online-11plus-mocks",
      town: "Online",
      address: "Online",
      postcode: "--",
      phone: "(0)2039808244",
      email: "support@sehyog.co.uk",
      image:
        "https://cdn2.exam.net/website-2025-11-05/wp-content/uploads/2024/03/examnet-student-doing-homework.png",
      distance: 0.0,
      services: [
        "11+ Mock Exams",
        "GCSE Mock Exams",
        "SAT Mock Exam",
        "Online",
      ],
      description:
        "Our Online Exams are available to book and attenend from home at any point of time",
      openingHours: [
        { day: "Monday", time: "9:00 AM - 7:00 PM" },
        { day: "Tuesday", time: "9:00 AM - 7:00 PM" },
        { day: "Wednesday", time: "9:00 AM - 7:00 PM" },
        { day: "Thursday", time: "9:00 AM - 7:00 PM" },
        { day: "Friday", time: "9:00 AM - 6:00 PM" },
        { day: "Saturday", time: "9:00 AM - 7:00 PM" },
        { day: "Sunday", time: "9:00 AM - 7:00 PM" },
      ],
      photos: [
        "assets/images/brentwood-1.jpg",
        "assets/images/brentwood-2.jpg",
        "assets/images/brentwood-3.jpg",
        "assets/images/brentwood-4.jpg",
      ],
      faqs: [
        {
          question: "Where to book Online Tests?",
          answer: "Visit the Mock Test Calender and Book.",
        },
        {
          question: "What subjects are covered in the mock exams?",
          answer:
            "For 11+, mocks usually include English, Mathematics, Verbal Reasoning, and Non-Verbal/Spatial Reasoning (depending on the exam board). GCSE mocks are offered in core subjects such as English, Mathematics, Computer Science, and Science, with additional subjects available at selected centres.",
        },
        {
          question: "Who writes the mock exam papers?",
          answer:
            "Papers are created by experienced subject specialists and reviewed to ensure accuracy, relevance, and appropriate exam standards.",
        },
        {
          question: "Are the mocks suitable for all ability levels?",
          answer:
            "Yes. The mock exams are designed to be accessible while still providing meaningful challenge and insight into a student’s current exam readiness.",
        },
        {
          question: "How do I book a mock exam?",
          answer:
            "Bookings can be made online via our website www.sehyog.co.uk. Places are limited and allocated on a first-come, first-served basis.",
        },
        {
          question: "What is included in the mock exam fee?",
          answer:
            "The fee includes the exam sitting, professional invigilation, marking, a detailed results report, and a doubt session for 11+ candidates only.",
        },
        {
          question: "Are refunds available if my child cannot attend?",
          answer:
            "Refund and rescheduling policies are clearly outlined during the booking process and vary depending on the notice given.",
        },
        {
          question: "How long do the exams last?",
          answer:
            "Exam durations match real exam timings and vary depending on the subject and level (11+ or GCSE).",
        },
        {
          question: "When will results be available?",
          answer:
            "Results are usually released within a specified timeframe after the exam (e.g. 3–5 days), depending on the exam type and number of students participating. In most cases, results are shared within 48 hours (T&C apply).",
        },
        {
          question: "What feedback will we receive?",
          answer:
            "Parents receive a detailed performance breakdown including scores, rank, percentile, average score, max/min score, strengths, areas for improvement, and overall exam readiness.",
        },
        {
          question: "Will rankings or comparisons be provided?",
          answer:
            "Where appropriate, results may include cohort comparisons or percentile rankings to give context to performance.",
        },
        {
          question:
            "Can the mock results be used to guide further preparation?",
          answer:
            "Absolutely. The results are designed to help identify gaps in knowledge and inform targeted revision or tuition plans.",
        },
        {
          question: "Do you offer follow-up support after the mocks?",
          answer:
            "Yes. Optional feedback sessions, revision workshops, or tuition may be available depending on the centre.",
        },
      ],

      schedules: [
        {
          title: "GCSE Maths (Edexcel)",
          rows: [
            { test: "Maths P-1", date: "Sunday, 11 January 2026" },
            { test: "Maths P-2", date: "Sunday, 18 January 2026" },
            { test: "Maths P-3", date: "Sunday, 25 January 2026" },
          ],
        },

        {
          title: "GCSE English (AQA, OCR & Edexcel)",
          rows: [
            { test: "English Literature P-1", date: "Sunday, 1 February 2026" },
            { test: "English Language P-1", date: "Sunday, 8 February 2026" },
            { test: "English Language P-2", date: "Sunday, 22 February 2026" },
          ],
        },

        {
          title: "GCSE Physics (AQA, OCR & Edexcel)",
          rows: [
            { test: "Physics P-1", date: "Sunday, 1 March 2026" },
            { test: "Physics P-2", date: "Sunday, 8 March 2026" },
          ],
        },

        {
          title: "GCSE Chemistry (AQA, OCR & Edexcel)",
          rows: [
            { test: "Chemistry P-1", date: "Sunday, 15 March 2026" },
            { test: "Chemistry P-2", date: "Sunday, 22 March 2026" },
          ],
        },

        {
          title: "GCSE Biology (AQA, OCR & Edexcel)",
          rows: [{ test: "Biology P-2", date: "Sunday, 5 April 2026" }],
        },

        {
          title: "GCSE Computer Science (AQA, OCR & Edexcel)",
          rows: [
            { test: "Computer Science P-1", date: "Sunday, 12 April 2026" },
            { test: "Computer Science P-2", date: "Sunday, 19 April 2026" },
          ],
        },
      ],

      mapUrl:
        "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2478.789!2d0.3045!3d51.6217!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47d8a00baf21de75%3A0x52963a5addd52a99!2sBrentwood%2C%20UK!5e0!3m2!1sen!2sus!4v1234567890",
    },
  ];

  constructor() {}

  getAllLocations(): TutionLocation[] {
    return this.locations;
  }

  getLocationByTown(town: string): TutionLocation {
    return this.locations.find((loc) => loc.id === town.toLowerCase());
  }

  searchLocations(searchTerm: string): TutionLocation[] {
    if (!searchTerm) {
      return this.locations;
    }
    searchTerm = searchTerm.toLowerCase();
    return this.locations.filter(
      (loc) =>
        loc.town.toLowerCase().includes(searchTerm) ||
        loc.address.toLowerCase().includes(searchTerm) ||
        loc.postcode.toLowerCase().includes(searchTerm)
    );
  }

  filterByService(service: string): TutionLocation[] {
    if (service === "all") {
      return this.locations;
    }
    return this.locations.filter((loc) =>
      loc.services.some((s) => s.toLowerCase().includes(service.toLowerCase()))
    );
  }
}
